import { useEffect, useMemo, useRef, useState } from "react";
import type { ActionDisplay, ConnectorActionChange, ConnectorActionRow, ConnectorActionsDTO, ConnectorActionState, ConnectorDTO } from "../../../shared/types";
import { confirmDialog, Dialog } from "../../components/Dialog";
import { List, ListRow } from "../../components/List";
import { MoreOnScroll } from "../../components/MoreOnScroll";
import { PermissionChoice, PermissionMenu, PERMISSIONS } from "../../components/PermissionChoice";
import { Switch } from "../../components/Switch";
import { useTabParam } from "../../components/Tabs";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, Collapsible, ErrorText, Field, inputCls } from "../../components/ui";
import { get, patch } from "../../lib/api";
import { AREA_PAGE, ASK_OVER, changesFor, filterActions, groupActions, OPEN_AREAS_UP_TO, sharedState, withChanges, type ActionArea } from "../../lib/tool-permissions";

const SHOW = ["all", "changed"] as const;
const ADDED_OPTIONS = PERMISSIONS.filter((p) => p.value !== "allow");
const LOCKED_WORDS = { template: "Needs approval, on its own card", released: "Only on a send approved on its own card", person: "Goes through only with a person behind the run, no card" };

/** Why a row can't be set, in place of its control: an action the connector refuses points at Setup, where that is decided. */
function LockedLine({ row, onShowSetup }: { row: ConnectorActionRow; onShowSetup: () => void }) {
  const lock = row.lock!;
  if (lock.kind === "refused") return <span>Off: this connector's access rules don't allow it. <TextLink onClick={onShowSetup}>Setup</TextLink></span>;
  if (lock.kind === "rule") return <span className="break-all">Held by another rule: <span className="font-mono">{lock.by}</span></span>;
  return <span>{LOCKED_WORDS[lock.kind]}</span>;
}

/** One action: its name with its call small beside it, and what it needs. Its name may be a vendor's: drawn as text, as everything here is. */
function ActionRow({ row, save, onShowSetup }: { row: ConnectorActionRow; save: (changes: ConnectorActionChange[]) => void; onShowSetup: () => void }) {
  const set = (state: ConnectorActionState, askOnSchedules = row.askOnSchedules) => save([{ method: row.method, paths: row.paths, state, ...(state === "approval" && askOnSchedules ? { askOnSchedules: true } : {}) }]);
  return (
    <ListRow
      className="border-t border-line"
      title={
        <>
          <span className="min-w-0 text-sm break-words">{row.name}</span>
          <span className="min-w-0 font-mono text-tiny break-all text-faint" title={row.paths.join("\n")}>
            {row.method} {row.paths[0]}{row.paths.length > 1 ? ` +${row.paths.length - 1}` : ""}
          </span>
        </>
      }
      trailing={
        <>
          {row.lock ? <LockedLine row={row} onShowSetup={onShowSetup} /> : (
            <PermissionChoice label={row.name} value={row.state} onChange={(state) => set(state)} options={row.added ? ADDED_OPTIONS : PERMISSIONS} />
          )}
          {row.added && row.lock?.kind !== "person" && <Button size="sm" variant="danger" onClick={() => set("allow")}>Remove</Button>}
        </>
      }
    >
      {row.state === "approval" && !row.lock && <Switch className="mt-1.5 max-w-64" size="sm" label={<span className="text-xs">Also ask on schedules</span>} checked={row.askOnSchedules} onChange={(on) => set("approval", on)} />}
    </ListRow>
  );
}

/** A fold's header: its name, how many, and the dropdown that sets them all. */
function GroupHead({ rows, setAll, label }: { rows: ConnectorActionRow[]; setAll: (rows: ConnectorActionRow[], state: ConnectorActionState) => void; label: string }) {
  const shared = sharedState(rows);
  return shared ? <PermissionMenu label={`Set everything in ${label}`} value={shared} onChange={(state) => setAll(rows, state)} /> : null;
}

/** An area's actions: the first 50, then 50 more as the end comes into view. */
function AreaRows({ area, save, onShowSetup }: { area: ActionArea; save: (changes: ConnectorActionChange[]) => void; onShowSetup: () => void }) {
  const [shown, setShown] = useState(AREA_PAGE);
  return (
    <>
      {area.rows.slice(0, shown).map((row) => <ActionRow key={`${row.method} ${row.paths[0]}`} row={row} save={save} onShowSetup={onShowSetup} />)}
      <MoreOnScroll onMore={() => setShown((n) => n + AREA_PAGE)} done={shown >= area.rows.length} loading={false} />
    </>
  );
}

/** An action the list doesn't have, or a custom connector's. Its name is the rule's label, so it's what an agent and a card call it. */
function AddAction({ onClose, onAdd }: { onClose: () => void; onAdd: (change: ConnectorActionChange) => Promise<void> }) {
  const [draft, setDraft] = useState({ method: "POST", path: "", name: "", display: "json" as ActionDisplay });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = draft.path.trim().startsWith("/") && draft.name.trim();
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await onAdd({ method: draft.method, paths: [draft.path.trim()], state: "approval", name: draft.name.trim(), display: draft.display });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Add an action"
      description="For an address the list doesn't have. It starts on Needs approval."
      busy={busy}
      primary={<Button variant="primary" disabled={!ready || busy} onClick={add}>Add action</Button>}
    >
      <div className="space-y-3">
        <Field label="Method">
          <select className={inputCls} value={draft.method} onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
            {["POST", "PUT", "PATCH", "DELETE", "GET"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Path" hint="From the tool's address. * stands for one part of the path, ** for any number.">
          <input className={`${inputCls} font-mono`} value={draft.path} placeholder="/invoices/*/send" onChange={(e) => setDraft({ ...draft, path: e.target.value })} />
        </Field>
        <Field label="Name" hint="What agents and the approval card call it.">
          <input className={inputCls} value={draft.name} placeholder="Send an invoice" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label="Show the call on its card as">
          <select className={inputCls} value={draft.display} onChange={(e) => setDraft({ ...draft, display: e.target.value as ActionDisplay })}>
            <option value="json">Its fields</option>
            <option value="email-raw">An email</option>
          </select>
        </Field>
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

/**
 * A connector's Tool permissions tab (AIT-111): every action it knows, by group and area, each Always allow, Needs approval
 * or Block. It only adds brakes: a save names actions and never a policy, and what the connector refuses stays locked.
 * Each change saves at once, in the order made, and the rows are read again once none is left.
 */
export function ToolPermissions({ connector, onSaved, onShowSetup }: { connector: ConnectorDTO; onSaved: () => Promise<void> | void; onShowSetup: () => void }) {
  const [data, setData] = useState<ConnectorActionsDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [show, setShow] = useTabParam(SHOW, "show");
  const [adding, setAdding] = useState(false);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const waiting = useRef(0);
  const reads = useRef(0);
  // A read older than the last click says nothing about it, so only the latest counts, and never while a save is on its way.
  const load = () => {
    const read = ++reads.current;
    return get<ConnectorActionsDTO>(`/api/connectors/${connector.id}/actions`).then(
      (fresh) => { if (read === reads.current && !waiting.current) setData(fresh); },
      (err: Error) => setError(err.message),
    );
  };
  useEffect(() => {
    if (!waiting.current) void load();
  }, [connector.id, connector.updatedAt]);

  const save = (changes: ConnectorActionChange[]) => {
    setError(null);
    setData((d) => d && { ...d, actions: withChanges(d.actions, changes) });
    waiting.current += 1;
    reads.current += 1;
    queue.current = queue.current.then(async () => {
      let failed = false;
      await patch(`/api/connectors/${connector.id}`, { actions: changes }).catch((err: Error) => {
        failed = true;
        setError(err.message);
      });
      waiting.current -= 1;
      if (failed) await load();
      if (!waiting.current) await onSaved();
    });
  };
  const setAll = async (rows: ConnectorActionRow[], state: ConnectorActionState) => {
    const changes = changesFor(rows, state);
    if (!changes.length) return;
    const word = PERMISSIONS.find((p) => p.value === state)!.label;
    if (changes.length > ASK_OVER && !(await confirmDialog({ title: `Set ${changes.length} actions to ${word}?`, body: "Each one gets a rule of its own, so each can still be changed alone.", confirm: `Set ${changes.length} actions` }))) return;
    save(changes);
  };
  const add = async (change: ConnectorActionChange) => {
    await patch(`/api/connectors/${connector.id}`, { actions: [change] });
    await onSaved();
  };

  const actions = data?.actions ?? [];
  const searching = Boolean(query.trim()) || show === "changed";
  const shown = useMemo(() => filterActions(actions, query, show === "changed"), [actions, query, show]);
  const groups = useMemo(() => groupActions(shown), [shown]);
  const openAreas = searching || actions.length <= OPEN_AREAS_UP_TO;
  const addButton = <Button size="sm" onClick={() => setAdding(true)}>+ Add an action</Button>;
  const empty = !data ? "Loading…"
    : data.oneAddress ? null
    : !actions.length ? `${connector.name} has no published list. Add the actions you want to set.`
    : "Nothing here matches.";

  return (
    <div className="space-y-3">
      <List
        title="Tool permissions"
        hint="Choose what agents may do with this tool. Anything not listed follows this connector's access rules."
        filters={actions.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <input className={`${inputCls} min-w-0 flex-1 basis-48`} value={query} placeholder="Search names and paths" aria-label="Search actions" onChange={(e) => setQuery(e.target.value)} />
            <Switch size="sm" label="Changed only" checked={show === "changed"} onChange={(on) => setShow(on ? "changed" : "all")} />
          </div>
        )}
        empty={data?.oneAddress ? undefined : empty}
        footer={data && !data.oneAddress ? addButton : undefined}
      >
        {data?.oneAddress && (
          <p key="one-address" className="px-4 py-3 text-sm text-muted">
            {connector.name} takes everything at one address, <span className="font-mono text-xs">{data.oneAddress}</span>, so its actions can't be told apart here. What agents may do with it is set by its access levels on <TextLink onClick={onShowSetup}>Setup</TextLink>.
          </p>
        )}
        {groups.map((group) => (
          <div key={group.key} className="px-4 py-3">
            <Collapsible
              defaultOpen
              summary={<span className="flex min-w-0 items-center gap-2 text-sm font-medium">{group.title} <Badge>{group.rows.length}</Badge></span>}
              action={<GroupHead rows={group.rows} setAll={setAll} label={group.title} />}
            >
              {group.areas.map((area) => (
                <div key={`${area.area}-${openAreas}`} className="mt-2">
                  <Collapsible
                    defaultOpen={openAreas || area.rows.some((a) => a.state !== "allow")}
                    summary={<span className="flex min-w-0 items-center gap-2 text-sm">{area.area} <Badge>{area.rows.length}</Badge></span>}
                    action={<GroupHead rows={area.rows} setAll={setAll} label={area.area} />}
                  >
                    <AreaRows area={area} save={save} onShowSetup={onShowSetup} />
                  </Collapsible>
                </div>
              ))}
            </Collapsible>
          </div>
        ))}
      </List>
      <ErrorText error={error} />
      {adding && <AddAction onClose={() => setAdding(false)} onAdd={add} />}
    </div>
  );
}
